import moment from 'moment';
import TrendAnalysisConstants from '../messages/TrendAnalysisConstants';
import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import axios from 'axios';
import properties from '../properties';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Grid } from '@mui/material';
import { ContractChipList } from '../../_components/atoms/ComparisonTable';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { formatDate } from './helper';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/** A single common-submetric entry — now carries its own color. */
export interface SubmetricEntry {
  name: string;
  color: string;
}

export interface metricsLevel2Data {
  name: string;
  color: string;
  /** Each element is { name, color } instead of a plain string. */
  submetrics?: SubmetricEntry[];
}

export interface metricsLevel1Data {
  name: string;
  identifier: string;
  options: metricsLevel2Data[];
}

export interface trendSeriesData {
  unit: string;
  chartType: string;
  chartStyle: string;
  metrics: metricsLevel1Data[];
}

export interface ChartConfigDropdownsProps {
  axisLabel: string;
  axisData: trendSeriesData;
  setAxisData: React.Dispatch<React.SetStateAction<trendSeriesData>>;
  otherAxisData: trendSeriesData;
  isSecondary: boolean;
  showSwitch: boolean;
  switchChecked: boolean;
   
  onSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface MetricConfigProps {
  axisData: trendSeriesData;
  setAxisData: React.Dispatch<React.SetStateAction<trendSeriesData>>;
  isSecondary: boolean;
  selectedMetricOptions: any;
}

export interface ColorPickerCircleProps {
  color: string;
   
  onChange: (newColor: string) => void;
}

export interface TrendAnalysisLocationProps {
  state: { trend_id?: string };
}

export interface TrendAnalysisLibraryLocationProps {
  state: {
    trend_id?: string;
    triggerEdit?: any;
  };
}

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

export const TREND_ANALYSIS_TABLE_COLUMNS = [
  { name: 'Analysis Name' },
  { name: 'Primary Axis' },
  { name: 'Secondary Axis' },
  { name: 'Time Grain' },
  { name: 'Date Range' },
  { name: 'Created By' },
];

export const MENU_OPTIONS = ['View Trend', 'Edit', 'Delete'];

// ─────────────────────────────────────────────────────────────
// Time grain helpers
// ─────────────────────────────────────────────────────────────

export const getDynamicTimeGrainOptions = ({
  noDateFilter,
  incurredStartDate,
  incurredEndDate,
  paidStartDate,
  paidEndDate,
  isIncurredDate,
  isPaidDate,
  aggregationParameter,
  minIncurred,
  maxIncurred,
  minPaid,
  maxPaid,
}: any) => {
  const startIncurred = noDateFilter ? minIncurred : incurredStartDate;
  const endIncurred = noDateFilter ? maxIncurred : incurredEndDate;
  const startPaid = noDateFilter ? minPaid : paidStartDate;
  const endPaid = noDateFilter ? maxPaid : paidEndDate;

  let selectedStart = '';
  let selectedEnd = '';

  if (isIncurredDate && isPaidDate) {
    if (aggregationParameter === 'paid') {
      selectedStart = startPaid;
      selectedEnd = endPaid;
    } else {
      selectedStart = startIncurred;
      selectedEnd = endIncurred;
    }
  } else if (isPaidDate) {
    selectedStart = startPaid;
    selectedEnd = endPaid;
  } else if (isIncurredDate) {
    selectedStart = startIncurred;
    selectedEnd = endIncurred;
  }

  if (!selectedStart || !selectedEnd) {
    return [];
  }

  const startMoment = moment(selectedStart, 'YYYY-MM-DD');
  const endMoment = moment(selectedEnd, 'YYYY-MM-DD');
  const months = endMoment.diff(startMoment, 'months') + 1;
  const quarters = endMoment.diff(startMoment, 'quarters') + 1;

  if (months < 6) {
    return ['Monthly'];
  }
  if (months <= 24) {
    return ['Monthly', 'Quarterly', 'Semi Annual', 'Annual'];
  }

  const opts: string[] = ['Semi Annual', 'Annual'];
  if (quarters <= 12) {
    opts.unshift('Quarterly');
  }
  return opts;
};

// ─────────────────────────────────────────────────────────────
// Chart type / style helpers
// ─────────────────────────────────────────────────────────────

export const getFilteredChartTypes = (
  otherType: string,
  isSecondaryAxis: boolean,
) =>
  TrendAnalysisConstants.CHART_TYPE_OPTIONS.map((item) => item.type).filter(
    (type) => type !== otherType && (!isSecondaryAxis || type !== 'Bar'),
  );

export const getFilteredChartStyles = (
  chartType: string,
  otherChartType: string,
  isSecondaryAxis: boolean,
) => {
  const styles =
    TrendAnalysisConstants.CHART_TYPE_OPTIONS.find(
      (item) => item.type === chartType,
    )?.styles || [];

  if (isSecondaryAxis) {
    if (chartType === 'Column' && otherChartType === 'Area') {
      return ['Grouped Column'];
    }
    if (chartType === 'Area' && otherChartType === 'Column') {
      return ['Multi Series Area'];
    }
  }
  return styles;
};

// ─────────────────────────────────────────────────────────────
// Dropdown option helpers
// ─────────────────────────────────────────────────────────────

export const getMetricDropdownOptions = (
  unit: string,
  metrics: metricsLevel1Data[],
  currentMetric: string,
  metricOptions: any[],
): string[] => {
  const unitBlock = metricOptions.find((item) => item.unit === unit);
  if (!unitBlock) {
    return [];
  }
  const updated = unitBlock.metrics
    .filter((temp: any) => !metrics.some((opt) => opt.name === temp.type))
    .map((item: any) => item.type);
  return currentMetric ? [currentMetric, ...updated] : updated;
};

export const getSubMetricDropdownOptions = (
  unit: string,
  metricType: string,
  options: metricsLevel2Data[],
  currentOption: string,
  metricOptions: any,
): string[] => {
  const unitBlock = metricOptions.find((item: any) => item.unit === unit);
  if (!unitBlock) {
    return [];
  }
  const metricBlock = unitBlock.metrics.find((m: any) => m.type === metricType);
  const updated = metricBlock
    ? metricBlock.options.filter(
        (temp: any) => !options.some((opt) => opt.name === temp),
      )
    : [];
  return currentOption ? [currentOption, ...updated] : updated;
};

// ─────────────────────────────────────────────────────────────
// Color utilities
// ─────────────────────────────────────────────────────────────

export const getRandomColor = (seed?: string): string => {
  const goldenRatio = 0.618033988749895;

  if (seed) {
    let hash = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      hash ^= seed.charCodeAt(i);
      hash = (hash * 16777619) >>> 0;
    }
    const hue = ((hash / 0xffffffff + goldenRatio) % 1) * 360;
    return hslToHex(hue, 65, 50);
  }

  const hue = (Math.random() + goldenRatio) % 1;
  return hslToHex(hue * 360, 65, 50);
};

const hslToHex = (h: number, s: number, l: number): string => {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) =>
    lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};

export const ColorPickerCircle: React.FC<ColorPickerCircleProps> = ({
  color,
  onChange,
}) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={() => setShowPicker(!showPicker)}
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: color || '#3b82f6',
          border: '2px solid #ccc',
          cursor: 'pointer',
        }}
      />
      {showPicker && (
        <div style={{ position: 'absolute', zIndex: 2, top: '45px' }}>
          <HexColorPicker color={color || '#3b82f6'} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────

export const getCostTrend = async (payload: any, signal: AbortSignal) => {
  const data = await axios
    .post(
      properties.aiqBaseUrl +
        properties.deepDiveBaseUrl +
        properties.deepDiveCostTrend,
      payload,
      { signal },
    )
    .then((res: any) => res.data.data);
  return data;
};

export const getTotalMembersApi = async (
  startDate: string,
  endDate: string,
  employerGroup: string,
  searchParams: any,
  type?: string,
) =>
  axios.post(
    properties.aiqBaseUrl +
      properties.summaryBaseUrl +
      properties.searchCohortMembers,
    {
      startDate,
      endDate,
      eg_nid: employerGroup,
      searchParams,
      type: type ?? 'data',
    },
  );

// ─────────────────────────────────────────────────────────────
// FusionChart type resolution
// ─────────────────────────────────────────────────────────────

export const getFusionChartType = (
  isSecondaryAxis: boolean,
  primaryAxisData: trendSeriesData,
  secondaryAxisData?: trendSeriesData,
) => {
  if (!isSecondaryAxis) {
    const { chartType, chartStyle } = primaryAxisData || {};
    switch (chartType?.toLowerCase()) {
      case 'column':
        return chartStyle?.toLowerCase() === 'stacked column'
          ? 'stackedcolumn2d'
          : 'mscolumn2d';
      case 'bar':
        return chartStyle?.toLowerCase() === 'stacked column'
          ? 'stackedbar2d'
          : 'msbar2d';
      case 'line':
        return 'msline';
      case 'area':
        return chartStyle?.toLowerCase() === 'stacked area'
          ? 'stackedarea2d'
          : 'msarea';
      default:
        return 'mscolumn2d';
    }
  }

  const primaryType = primaryAxisData?.chartType?.toLowerCase();
  const secondaryType = secondaryAxisData?.chartType?.toLowerCase();
  const primaryStyle = primaryAxisData?.chartStyle?.toLowerCase();
  const secondaryStyle = secondaryAxisData?.chartStyle?.toLowerCase();

  if (
    (primaryType === 'column' && secondaryType === 'line') ||
    (secondaryType === 'column' && primaryType === 'line')
  ) {
    return 'msstackedcolumn2dlinedy';
  }

  if (
    (primaryType === 'column' && secondaryType === 'area') ||
    (secondaryType === 'column' && primaryType === 'area')
  ) {
    return 'mscombidy2d';
  }

  if (
    (primaryType === 'line' && secondaryType === 'area') ||
    (secondaryType === 'line' && primaryType === 'area')
  ) {
    if (secondaryStyle === 'stacked area' || primaryStyle === 'stacked area') {
      return 'stackedarea2dlinedy';
    }
    if (
      secondaryStyle === 'multi series area' ||
      primaryStyle === 'multi series area'
    ) {
      return 'mscombidy2d';
    }
  }

  return 'mscombidy2d';
};

// ─────────────────────────────────────────────────────────────
// buildFusionChartDataSource
// ─────────────────────────────────────────────────────────────

export const buildFusionChartDataSource = (
  responseTrendArray: any[] = [],
  primaryAxisData: trendSeriesData,
  secondaryAxisData: trendSeriesData,
  timeGrain: string,
  chartType: string,
) => {
  let categories: any = [];

  const buildCategories = (seriesData: any) => [
    {
      category: (seriesData || []).map((item: any) => {
        let label = '';

        switch (timeGrain) {
          case 'Monthly': {
            const monthNames = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];

            label = `${monthNames[(item.month || 1) - 1]} <br><br> ${
              item.year || ''
            }`;

            break;
          }

          case 'Quarterly':
            label = `Q${item.quarter || ''} <br><br> ${item.year || ''}`;
            break;

          case 'Semi Annual':
            label = `H${item.half || ''} <br><br> ${item.year || ''}`;
            break;

          case 'Annual':
            label = `${item.year || ''}`;
            break;

          default: {
            const monthNames = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'May',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Oct',
              'Nov',
              'Dec',
            ];

            if (item.month) {
              label = `${monthNames[(item.month || 1) - 1]} <br><br> ${
                item.year || ''
              }`;
            } else if (item.quarter) {
              label = `Q${item.quarter} <br><br> ${item.year || ''}`;
            } else if (item.half) {
              label = `H${item.half} <br><br> ${item.year || ''}`;
            } else {
              label = `${item.year || ''}`;
            }
          }
        }

        return {
          label,
        };
      }),
    },
  ];

  const commonConfig: Record<string, any> = {
    theme: 'fusion',
    baseFont: 'Inter',
    bgColor: patientAccessTheme.palette.status.white,
    canvasBgColor: patientAccessTheme.palette.status.white,
    showBorder: '0',
    showPlotBorder: '0',
    showAlternateHGridColor: '0',
    divLineColor: patientAccessTheme.palette.grey[300],
    numDivLines: '4',
    showValues: '0',
    showLegend: '1',
    legendBgAlpha: '0',
    legendBorderAlpha: '0',
    legendShadow: '0',
    legendItemFontSize: '12',
    legendItemFontColor: patientAccessTheme.palette.text.mediumemphasis,
    drawCustomLegendIcon: '1',
    legendIconAlpha: '100',
    legendIconSides: '100',
    legendPadding: '16',
    animation: '0',
    showXAxisLine: '1',
    showYAxisLine: '1',
    xAxisLineColor: patientAccessTheme.palette.grey[300],
    yAxisLineColor: patientAccessTheme.palette.grey[300],
    labelFontColor: patientAccessTheme.palette.text.mediumemphasis,
    labelFontSize: '10',
    labelPadding: '10',
    usePlotGradientColor: '0',
    toolTipColor: patientAccessTheme.palette.status.white,
    toolTipBgColor: patientAccessTheme.palette.grey[900],
    toolTipBgAlpha: '100',
    showToolTipShadow: '1',
    decimals: 0,
    forceDecimals: '1',
    thousandSeparator: ',',
    formatNumberScale: '0',
    formatNumber: '1',
  };

  const getLineStyleConfig = (style: string) => {
    switch (style) {
      case 'Dashed Line':
        return {
          lineDashed: '1',
          lineDashLen: '6',
          lineDashGap: '4',
        };

      case 'Dotted Line':
        return {
          lineDashed: '1',
          lineDashLen: '1',
          lineDashGap: '2',
        };

      default:
        return {
          lineDashed: '0',
        };
    }
  };

  const resolveRenderType = (axisData: trendSeriesData) => {
    const type = (axisData?.chartType || '').toLowerCase();

    if (type.includes('line')) {
      return 'line';
    }

    if (type.includes('area')) {
      return 'area';
    }

    return 'column';
  };

  // -------------------------------------------------------
  // BUILD DATASET
  // -------------------------------------------------------
  const buildDataset = (axisData: trendSeriesData, isSecondary = false) => {
    const renderType = resolveRenderType(axisData);

    return (axisData?.metrics || []).flatMap((metric: any) =>
      (metric.options || []).flatMap((option: any) => {
        const metricType = metric.identifier;

        const trendBlocks = findTrendBlocks(responseTrendArray, metricType);

         
        return trendBlocks.flatMap((trendBlock: any) => {
          const seriesForOption = extractSeries(trendBlock, option?.name);

          if (!categories?.length && seriesForOption?.length) {
            categories = buildCategories(seriesForOption);
          }

          const dataSourceArray = Array.isArray(seriesForOption)
            ? seriesForOption
            : [];

          const cohortName = trendBlock?.cohortName ?? '';
          const cohortColor = trendBlock?.cohortColor ?? '';

          const datasets: any[] = [];

          // --------------------------------------------------
          // BASE SERIES
          // --------------------------------------------------
          const baseFieldKey = getFieldName(
            metricType,
            option.name,
            axisData.unit,
          );

           
          const baseDataPoints = dataSourceArray.map((item: any) => {
            const raw = Number(item?.[baseFieldKey] ?? 0);

            return {
              value: Number(raw || 0).toFixed(2),
            };
          });

          datasets.push({
            seriesName: `${option?.name}${
              cohortName ? ` - ${cohortName}` : ''
            }`,

            color: cohortColor || option?.color,

            plottooltext: `${option?.name}${
              cohortName ? ` - ${cohortName}` : ''
            } : $dataValue`,

            renderAs: renderType,

            showValues: '0',

            parentYAxis: isSecondary ? 'S' : 'P',

            ...getLineStyleConfig(axisData?.chartStyle),

            data: baseDataPoints,
          });

          // --------------------------------------------------
          // SUBMETRICS
          // --------------------------------------------------
           
          (option?.submetrics || []).forEach((submetric: SubmetricEntry) => {
            const subFieldKey = getFieldName(
              metricType,
              option.name,
              axisData.unit,
              submetric.name,
            );

             
            const submetricData = dataSourceArray.map((item: any) => {
              const raw = Number(item?.[subFieldKey] ?? 0);

              return {
                value: Number(raw || 0).toFixed(2),
              };
            });

            datasets.push({
              seriesName: `${option?.name} - ${submetric.name}${
                cohortName ? ` - ${cohortName}` : ''
              }`,

              color: cohortColor || submetric.color,

              plottooltext: `${option?.name} - ${submetric.name}${
                cohortName ? ` - ${cohortName}` : ''
              } : $dataValue`,

              renderAs: renderType,

              showValues: '0',

              parentYAxis: isSecondary ? 'S' : 'P',

              ...getLineStyleConfig(axisData?.chartStyle),

              data: submetricData,
            });
          });

          return datasets;
        });
      }),
    );
  };

  // -------------------------------------------------------
  // DUAL AXIS
  // -------------------------------------------------------
  if (chartType === 'msstackedcolumn2dlinedy') {
    const primaryDataset = buildDataset(primaryAxisData);

    const secondaryDataset = buildDataset(secondaryAxisData, true);

    const isPrimaryStacked = primaryAxisData?.chartStyle === 'Stacked Column';

    const isSecondaryStacked =
      secondaryAxisData?.chartStyle === 'Stacked Column';

    const isPrimaryGrouped = primaryAxisData?.chartStyle === 'Grouped Column';

    const isSecondaryGrouped =
      secondaryAxisData?.chartStyle === 'Grouped Column';

    const formattedPrimary = isPrimaryStacked
      ? [{ dataset: primaryDataset }]
      : isPrimaryGrouped
      ? primaryDataset.map((temp: any) => ({
          dataset: [temp],
        }))
      : primaryDataset;

    const formattedSecondary = isSecondaryStacked
      ? [{ dataset: secondaryDataset }]
      : isSecondaryGrouped
      ? secondaryDataset.map((temp: any) => ({
          dataset: [temp],
        }))
      : secondaryDataset;

    return {
      chart: {
        ...commonConfig,

        pYAxisName: primaryAxisData?.unit || '',

        sYAxisName: secondaryAxisData?.unit || '',

        numberPrefix: primaryAxisData?.unit === 'Cost' ? '$' : '',

        sNumberPrefix: secondaryAxisData?.unit === 'Cost' ? '$' : '',
      },

      categories,

      dataset:
        primaryAxisData.chartType === 'Column'
          ? formattedPrimary
          : formattedSecondary,

      lineset:
        primaryAxisData.chartType === 'Column'
          ? formattedSecondary
          : formattedPrimary,
    };
  }

  // -------------------------------------------------------
  // COMBO CHART
  // -------------------------------------------------------
  if (chartType === 'mscombidy2d' || chartType === 'stackedarea2dlinedy') {
    const primaryDataset = buildDataset(primaryAxisData);
    const secondaryDataset = buildDataset(secondaryAxisData, true);
    return {
      chart: {
        ...commonConfig,

        pYAxisName: primaryAxisData?.unit || '',

        sYAxisName: secondaryAxisData?.unit || '',

        numberPrefix: primaryAxisData?.unit === 'Cost' ? '$' : '',

        sNumberPrefix: secondaryAxisData?.unit === 'Cost' ? '$' : '',
      },

      categories,

      dataset: [...primaryDataset, ...secondaryDataset],
    };
  }

  // -------------------------------------------------------
  // SINGLE AXIS
  // -------------------------------------------------------
  const dataset = buildDataset(primaryAxisData);
  return {
    chart: {
      ...commonConfig,

      yAxisName: primaryAxisData?.unit || '',

      numberPrefix: primaryAxisData?.unit === 'Cost' ? '$' : '',
    },

    categories,

    dataset,
  };
};

// -------------------------------------------------------
// FIELD RESOLUTION
// -------------------------------------------------------
export const getFieldName = (
  metricType: string,
  optionName: string,
  unit: string,
  submetricName?: string,
) => {
  const METRIC_FIELD_MAP: Record<string, string> =
    TrendAnalysisConstants.METRIC_FIELD_MAP;

  const SUBMETRIC_FIELD_MAP: Record<string, string> = {
    'Per Member Per Month (PMPM)': 'Per Member Per Month (PMPM)',
    'Per Member Per Year (PMPY)': 'Per Member Per Year (PMPY)',
    'Per Employee Per Month (PEPM)': 'Per Employee Per Month (PEPM)',
    'Cost Per 100 Members': 'Cost Per 100 Members',
    'Cost Per 1000 Members': 'Cost Per 1000 Members',
  };

  // overall block
  if (metricType === 'overall') {
    const baseField = METRIC_FIELD_MAP[optionName] || 'total_paid_amount';

    if (submetricName) {
      return `${optionName}_${SUBMETRIC_FIELD_MAP[submetricName]}`;
    }

    return baseField;
  }

  // category-level submetrics
  if (submetricName) {
    return `${optionName}_${SUBMETRIC_FIELD_MAP[submetricName]}`;
  }

  switch (unit) {
    case 'Cost':
      return 'total_paid_amount';

    case 'Claim Count':
      return 'claimsCount';

    case 'Member Count':
      return 'memberCount';

    default:
      return 'total_paid_amount';
  }
};

// -------------------------------------------------------
// EXTRACT SERIES
// -------------------------------------------------------
export const extractSeries = (trendBlock: any, optionName: string) => {
  if (!trendBlock?.data) {
    return [];
  }

  // exact match
  if (Array.isArray(trendBlock.data?.[optionName])) {
    return trendBlock.data[optionName];
  }

  // case-insensitive fallback
  const matchedKey = Object.keys(trendBlock.data || {}).find(
    (key) => key.toLowerCase() === optionName?.toLowerCase(),
  );

  if (matchedKey && Array.isArray(trendBlock.data?.[matchedKey])) {
    return trendBlock.data[matchedKey];
  }

  return [];
};

// -------------------------------------------------------
// FIND TREND BLOCKS
// -------------------------------------------------------
export const findTrendBlocks = (
  responseTrendArray: any[] = [],
  trendType: string,
) => {
  return responseTrendArray.filter(
    (item: any) =>
      (item?.type || '').toLowerCase() === (trendType || '').toLowerCase(),
  );
};

// ─────────────────────────────────────────────────────────────
// formatTrendParameters
//
// Serialises UI state → API payload trends[].
// Submetrics emitted as { name, color } objects.
// ─────────────────────────────────────────────────────────────

export const formatTrendParameters = (
  primaryAxisData: trendSeriesData,
  secondaryAxisData?: trendSeriesData,
) => {
  const trends: any[] = [];

  const addTrend = (type: string, items: any[]) => {
    if (!items?.length) {
      return;
    }

    const existing = trends.find((t: any) => t.type === type);
    if (existing) {
      const merged = [...existing.options];
      items.forEach((item: any) => {
        if (!merged.some((m: any) => m.name === item.name)) {
          merged.push(item);
        }
      });
      existing.options = merged;
    } else {
      const uniqueItems: any[] = [];
      items.forEach((item: any) => {
        if (!uniqueItems.some((m: any) => m.name === item.name)) {
          uniqueItems.push(item);
        }
      });
      trends.push({ type, options: uniqueItems });
    }
  };

  /** Extract full option object; submetrics kept as { name, color }[] */
  const extractOptions = (metrics: metricsLevel1Data) =>
    (metrics.options || []).map((opt) => ({
      name: opt.name,
      color: opt.color,
      // Ensure each submetric is a { name, color } object
      submetrics: (opt.submetrics || []).map((s) =>
        typeof s === 'string'
          ? { name: s, color: getRandomColor(s) } // backward-compat guard
          : s,
      ),
    }));

  (primaryAxisData?.metrics || []).forEach((metricGroup) => {
    addTrend(metricGroup.identifier, extractOptions(metricGroup));
  });

  if (secondaryAxisData?.metrics?.length) {
    secondaryAxisData.metrics.forEach((metricGroup) => {
      addTrend(metricGroup.identifier, extractOptions(metricGroup));
    });
  }

  return trends;
};

// ─────────────────────────────────────────────────────────────
// downloadCostTrendCsv
// ─────────────────────────────────────────────────────────────

export const downloadCostTrendCsv = (
  responseTrendArray: any[] = [],
  primaryAxisData: trendSeriesData,
  secondaryAxisData: trendSeriesData,
  timeGrain: string,
) => {
  if (!responseTrendArray?.length) {
    return;
  }

  const extractAllSeriesFromAxis = (axisData: trendSeriesData) => {
    const outRows: any[] = [];

    (axisData?.metrics || []).forEach((metric: any) => {
      const metricType = metric.identifier;
      const trendBlocks = findTrendBlocks(responseTrendArray, metricType);

      (metric.options || []).forEach((option: any) => {
         
        trendBlocks.forEach((trendBlock: any) => {
          const seriesData: any[] = extractSeries(trendBlock, option?.name);

          const fieldKey = getFieldName(metricType, option.name, axisData.unit);

           
          seriesData.forEach((row) => {
            outRows.push({
              seriesName: option?.name,
              unit: axisData.unit,
              year: row.year,
              month: row?.month,
              quarter: row?.quarter,
              half: row?.half,
              value: row[fieldKey] ?? '',
            });
          });

          // Also emit a row per submetric so CSV includes all series
           
          (option?.submetrics || []).forEach((submetric: SubmetricEntry) => {
            const subFieldKey = getFieldName(
              metricType,
              option.name,
              axisData.unit,
              submetric.name,
            );
             
            seriesData.forEach((row) => {
              outRows.push({
                seriesName: `${option?.name} - ${submetric.name}`,
                unit: axisData.unit,
                year: row.year,
                month: row?.month,
                quarter: row?.quarter,
                half: row?.half,
                value: row[subFieldKey] ?? '',
              });
            });
          });
        });
      });
    });

    return outRows;
  };

  const primaryRows = extractAllSeriesFromAxis(primaryAxisData) ?? [];
  const secondaryRows = extractAllSeriesFromAxis(secondaryAxisData) ?? [];
  const finalRows = [...primaryRows, ...secondaryRows];
  if (!finalRows.length) {
    return;
  }

  const getTimeLabel = (row: any) => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    if (timeGrain === 'Monthly') {
      return `${monthNames[(row.month || 1) - 1]} ${row.year || ''}`;
    }
    if (timeGrain === 'Quarterly') {
      return `Q${row.quarter || ''} ${row.year || ''}`;
    }
    if (timeGrain === 'Semi Annual') {
      return `H${row.half || ''} ${row.year || ''}`;
    }
    if (timeGrain === 'Annual') {
      return `${row.year || ''}`;
    }
    if (row.month) {
      return `${monthNames[(row.month || 1) - 1]} ${row.year || ''}`;
    }
    if (row.quarter) {
      return `Q${row.quarter} ${row.year || ''}`;
    }
    return `${row.year || ''}`;
  };

  const headers = ['seriesName', 'unit', 'time', 'value'];
  const csvLines = [headers.join(',')];
  finalRows.forEach((r) =>
    csvLines.push([r.seriesName, r.unit, getTimeLabel(r), r.value].join(',')),
  );

  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'cost_trend.csv';
  a.click();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────
// formatTrendTableData — unchanged, no submetric rendering here
// ─────────────────────────────────────────────────────────────

export const formatTrendTableData = (data: any[]) => {
  const tableData: any[] = [];
  data.map((item: any) => {
    const otherData = [];
    otherData.push(
      <Grid style={{ width: '250px', overflow: 'hidden' }}>
        {item?.trendList?.length > 0 &&
          item.trendList.map((trend: any, index: number) => (
            <Grid style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
              <Typography
                fontSize="14px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                {trend?.primaryAxisData?.unit}
              </Typography>
              {trend?.primaryAxisData?.metrics?.length > 0 && (
                <ContractChipList
                  key={index}
                  selectedItems={
                     
                    trend.primaryAxisData.metrics.map((metric: any) => {
                      const options =
                        metric?.options?.length > 0
                          ? metric.options
                               
                              .map((opt: any) => opt.name)
                              .join(', ')
                          : '';
                      return options ? `${metric.name} - ${options}` : '';
                    }) || []
                  }
                />
              )}
            </Grid>
          ))}
      </Grid>,
    );
    otherData.push(
      <Grid style={{ width: '250px', overflow: 'hidden' }}>
        {item?.trendList?.length > 0 &&
          item.trendList.map((trend: any, index: number) => (
            <Grid style={{ display: 'flex', alignItems: 'center' }}>
              {trend?.isSecondaryAxis ? (
                <>
                  <Typography
                    fontSize="14px"
                    fontType="medium"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {trend?.secondaryAxisData?.unit}
                  </Typography>
                  {trend?.secondaryAxisData?.metrics?.length > 0 && (
                    <ContractChipList
                      key={index}
                      selectedItems={
                         
                        trend.secondaryAxisData.metrics.map((metric: any) => {
                          const options =
                            metric?.options?.length > 0
                              ? metric.options
                                   
                                  .map((opt: any) => opt.name)
                                  .join(', ')
                              : '';
                          return options ? `${metric.name} - ${options}` : '';
                        }) || []
                      }
                    />
                  )}
                </>
              ) : (
                '-'
              )}
            </Grid>
          ))}
      </Grid>,
    );
    otherData.push(
      <Grid style={{ width: '80px', overflow: 'hidden' }}>
        {item?.trendList?.length > 0 &&
          item.trendList.map((trend: any) => (
            <Grid style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                fontSize="14px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                styleProps={{ width: '80px' }}
              >
                {trend?.timeGrain}
              </Typography>
            </Grid>
          ))}
      </Grid>,
    );
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{ width: '190px' }}
      >
        {item?.aggregationParameter === 'paid'
          ? `${formatDate(item?.paidStartDate, 'DD MMM YYYY')} - ${formatDate(
              item?.paidEndDate,
              'DD MMM YYYY',
            )}`
          : `${formatDate(
              item?.incurredStartDate,
              'DD MMM YYYY',
            )} - ${formatDate(item?.incurredEndDate, 'DD MMM YYYY')}`}
      </Typography>,
    );
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{ width: '150px' }}
      >
        {item?.createdBy ?? '-'}
      </Typography>,
    );
    tableData.push({
      _id: item._id,
      company_name: item?.trendName,
      status: 'Ready',
      otherData,
    });
  });
  return tableData;
};
